import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Package, User, Lock, Loader2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { useStore } from '../context/StoreContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StaffLogin = ({ storeToken, onStaffLogin, onBackToStoreLogin }) => {
  const [initials, setInitials] = useState('');
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const navigate = useNavigate();
  const { selectedStore, storeName, isMultiStore } = useStore();

  // Quick-select shows only the people who work at this till's store
  const fetchStaffList = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/staff/active-list`, {
        params: { store: selectedStore },
        headers: { Authorization: `Bearer ${storeToken}` }
      });
      setStaffList(response.data || []);
    } catch (error) {
      // Staff list may not be available yet
      setStaffList([]);
    }
  }, [storeToken, selectedStore]);

  useEffect(() => {
    fetchStaffList();
  }, [fetchStaffList]);

  // Handle staff selection from list
  const handleSelectStaff = (staff) => {
    setSelectedStaff(staff);
    setInitials(staff.initials);
    setPin('');
  };

  // Handle PIN input (only 2 digits)
  const handlePinChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 2);
    setPin(value);
  };

  // Handle initials input (only letters, max 3)
  const handleInitialsChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3);
    setInitials(value);
    setSelectedStaff(null);
  };

  // Handle login
  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!initials || initials.length < 2) {
      toast.error('Please enter your initials (2-3 letters)');
      return;
    }
    
    if (!pin || pin.length !== 2) {
      toast.error('Please enter your 2-digit PIN');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/staff-login`, {
        initials,
        pin,
        store_token: storeToken
      });

      // Save staff session to localStorage so AuthContext can pick it up
      localStorage.setItem('staffToken', response.data.access_token);
      localStorage.setItem('activeStaff', JSON.stringify(response.data.staff));

      toast.success(`Welcome, ${response.data.staff.name}!`);
      onStaffLogin(response.data);
      
      // Force a small delay then navigate to ensure state updates
      setTimeout(() => {
        navigate('/pos');
      }, 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid initials or PIN');
    } finally {
      setLoading(false);
    }
  };

  // Handle PIN pad click
  const handlePinPadClick = (digit) => {
    if (pin.length < 2) {
      setPin(prev => prev + digit);
    }
  };

  const clearPin = () => setPin('');

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2D3E2F] to-[#1E2B20] flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-0 shadow-2xl bg-white">
        <CardContent className="p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#2D3E2F] rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <Package className="w-9 h-9 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-[#1B5E20]" style={{fontFamily: 'Work Sans, sans-serif'}}>
              Staff Login
            </h1>
            <p className="text-slate-500 mt-1">Enter your initials and PIN</p>
            {isMultiStore && (
              <p className="text-xs font-medium text-[#1B5E20] mt-2" data-testid="staff-login-store">{storeName(selectedStore)} till</p>
            )}
          </div>

          {/* Staff Quick Select (if staff list available) */}
          {staffList.length > 0 && (
            <div className="mb-6">
              <p className="text-xs font-medium text-slate-500 mb-2 uppercase">Quick Select</p>
              <div className="flex flex-wrap gap-2">
                {staffList.map(staff => (
                  <button
                    key={staff.id}
                    onClick={() => handleSelectStaff(staff)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                      selectedStaff?.id === staff.id
                        ? 'bg-[#2D3E2F] text-white'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                    data-testid={`staff-quick-select-${staff.id}`}
                  >
                    {staff.initials} - {staff.name.split(' ')[0]}
                  </button>
                ))}
              </div>
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-5">
            {/* Initials Input */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Initials</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  value={initials}
                  onChange={handleInitialsChange}
                  placeholder="e.g., JD"
                  className="pl-10 text-center text-2xl font-bold tracking-widest h-14 uppercase"
                  maxLength={3}
                  data-testid="staff-initials-input"
                />
              </div>
            </div>

            {/* PIN Input */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">2-Digit PIN</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="password"
                  value={pin}
                  onChange={handlePinChange}
                  placeholder="••"
                  className="pl-10 text-center text-2xl tracking-[0.5em] h-14"
                  maxLength={2}
                  inputMode="numeric"
                  data-testid="staff-pin-input"
                />
              </div>
            </div>

            {/* PIN Pad */}
            <div className="grid grid-cols-3 gap-2">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, null, 0, 'C'].map((digit, idx) => (
                digit === null ? (
                  <div key={idx} />
                ) : digit === 'C' ? (
                  <button
                    key={idx}
                    type="button"
                    onClick={clearPin}
                    className="h-14 rounded-lg bg-red-100 text-red-600 font-bold text-lg hover:bg-red-200 transition-colors"
                    data-testid="pin-clear-button"
                  >
                    C
                  </button>
                ) : (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => handlePinPadClick(digit.toString())}
                    className="h-14 rounded-lg bg-slate-100 text-slate-700 font-bold text-lg hover:bg-slate-200 transition-colors"
                    data-testid={`pin-pad-${digit}`}
                  >
                    {digit}
                  </button>
                )
              ))}
            </div>

            {/* Login Button */}
            <Button
              type="submit"
              disabled={loading || initials.length < 2 || pin.length !== 2}
              className="w-full h-12 bg-[#2D3E2F] hover:bg-[#1E2B20] text-white font-semibold text-lg"
              data-testid="staff-login-button"
            >
              {loading ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Logging In...</>
              ) : (
                <>Clock In <ArrowRight className="w-5 h-5 ml-2" /></>
              )}
            </Button>
          </form>

          {/* Back to Store Login */}
          <div className="mt-6 text-center">
            <button
              onClick={onBackToStoreLogin}
              className="text-sm text-slate-500 hover:text-slate-700"
              data-testid="back-to-store-login"
            >
              Switch Store Account
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default StaffLogin;
